import { Select, Input, Button, Space, Typography } from "antd";
import { PlusOutlined, CloseOutlined } from "@ant-design/icons";

const { Text } = Typography;

const MATCH_OPTIONS = [
  { value: "AND", label: "ALL" },
  { value: "OR", label: "ANY" },
];

// Field/operator whitelist — mirrors config/trace_log_filter_tree.php (requirements/API055).
// date_from/date_to are intentionally left out of the builder: they're sent as
// the separate top-level date range instead, to avoid the leaf/flat-param
// collision the API rejects with 422 (IMPR002/PLAN095 negative scenario 26).
export const FIELD_CONFIG = {
  status: {
    label: "Status",
    operators: [{ value: "is", label: "is" }],
  },
  module: {
    label: "Module",
    operators: [{ value: "is", label: "is" }],
  },
  session_id: {
    label: "Session ID",
    operators: [
      { value: "is", label: "is" },
      { value: "in", label: "is any of" },
    ],
  },
  step_name: {
    label: "Step Name",
    operators: [
      { value: "is", label: "is" },
      { value: "contains", label: "contains" },
    ],
  },
  property: {
    label: "Property",
    operators: [
      { value: "is", label: "is" },
      { value: "prefix", label: "starts with" },
      { value: "contains", label: "contains" },
    ],
  },
  keyword: {
    label: "Keyword",
    operators: [{ value: "contains", label: "contains" }],
  },
};

const STATUS_OPTIONS = [
  { value: "success", label: "Confirmed" },
  { value: "pending", label: "In Progress" },
  { value: "failed", label: "Failed" },
  { value: "partial", label: "Partial" },
  { value: "abandoned", label: "Abandoned" },
  { value: "expired", label: "Expired" },
  { value: "all", label: "All" },
];

const MAX_DEPTH = 5;
const MAX_CONDITIONS = 25;

let idCounter = 0;
const nextId = () => `f${++idCounter}-${Date.now()}`;

function defaultValueForField(field, operator) {
  switch (field) {
    case "status":
      return "success";
    case "property":
      return { key: "", value: "" };
    case "session_id":
      return operator === "in" ? [] : "";
    default:
      return "";
  }
}

export function makeLeaf(field = "keyword") {
  const config = FIELD_CONFIG[field];
  const operator = config.operators[0].value;
  return {
    id: nextId(),
    type: "leaf",
    field,
    operator,
    value: defaultValueForField(field, operator),
  };
}

export function makeGroup(operator = "AND", conditions) {
  return {
    id: nextId(),
    type: "group",
    operator,
    conditions: conditions || [makeLeaf()],
  };
}

export function createEmptyFilterTree() {
  return makeGroup("AND");
}

/** Count leaf conditions across the tree (mirrors the API's max_conditions check). */
export function countLeaves(node) {
  if (!node) return 0;
  if (node.type === "leaf") return 1;
  return node.conditions.reduce((sum, c) => sum + countLeaves(c), 0);
}

/** Nesting depth — a bare leaf is depth 1, each wrapping group adds 1. */
export function treeDepth(node) {
  if (!node) return 0;
  if (node.type === "leaf") return 1;
  if (!node.conditions.length) return 1;
  return 1 + Math.max(...node.conditions.map(treeDepth));
}

function serializeLeaf(node) {
  const { field, operator, value } = node;

  if (field === "property") {
    const key = value?.key?.trim();
    const val = value?.value;
    if (!key || val === undefined || val === "") return null;
    return { field, operator, value: { key, value: val } };
  }

  if (field === "session_id" && operator === "in") {
    const arr = (Array.isArray(value) ? value : []).map((v) => v.trim()).filter(Boolean);
    if (arr.length === 0) return null;
    return { field, operator, value: arr };
  }

  if (typeof value !== "string" || !value.trim()) return null;
  return { field, operator, value: value.trim() };
}

/**
 * Convert the builder's local tree (with UI-only `id`/`type` fields) into the
 * API's `{operator, conditions}` / `{field, operator, value}` shape. Drops
 * incomplete leaves and collapses empty/single-child groups. Returns null
 * when nothing valid remains (caller should omit `filters` entirely then).
 */
export function serializeFilterTree(node) {
  if (!node) return null;

  if (node.type === "group") {
    const conditions = node.conditions.map(serializeFilterTree).filter(Boolean);
    if (conditions.length === 0) return null;
    if (conditions.length === 1) return conditions[0];
    return { operator: node.operator, conditions };
  }

  return serializeLeaf(node);
}

function renderValueInput(field, operator, value, onChange) {
  if (field === "status") {
    return (
      <Select style={{ width: "100%" }} value={value} onChange={onChange} options={STATUS_OPTIONS} />
    );
  }
  if (field === "module") {
    return (
      <Input
        style={{ width: "100%" }}
        placeholder="e.g. booking"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  if (field === "session_id") {
    if (operator === "in") {
      return (
        <Select
          mode="tags"
          style={{ width: "100%" }}
          placeholder="Enter session IDs, press enter"
          value={value}
          onChange={onChange}
          tokenSeparators={[","]}
        />
      );
    }
    return (
      <Input
        style={{ width: "100%" }}
        placeholder="Session ID"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  if (field === "step_name") {
    return (
      <Input
        style={{ width: "100%" }}
        placeholder="e.g. create-appointment"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  if (field === "keyword") {
    return (
      <Input
        style={{ width: "100%" }}
        placeholder="Free text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  if (field === "property") {
    return (
      <Space.Compact style={{ width: "100%" }}>
        <Input
          style={{ width: "45%" }}
          placeholder="key, e.g. clinic_id"
          value={value?.key || ""}
          onChange={(e) => onChange({ ...value, key: e.target.value })}
        />
        <Input
          style={{ width: "55%" }}
          placeholder="value"
          value={value?.value || ""}
          onChange={(e) => onChange({ ...value, value: e.target.value })}
        />
      </Space.Compact>
    );
  }
  return null;
}

function FilterLeaf({ node, onChange, onRemove }) {
  const config = FIELD_CONFIG[node.field];

  const handleFieldChange = (field) => {
    const nextConfig = FIELD_CONFIG[field];
    const operator = nextConfig.operators[0].value;
    onChange({ ...node, field, operator, value: defaultValueForField(field, operator) });
  };

  const handleOperatorChange = (operator) => {
    onChange({ ...node, operator, value: defaultValueForField(node.field, operator) });
  };

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 12 }}>
      <Select
        style={{ width: 140 }}
        value={node.field}
        onChange={handleFieldChange}
        options={Object.entries(FIELD_CONFIG).map(([value, c]) => ({ value, label: c.label }))}
      />
      <Select
        style={{ width: 150 }}
        value={node.operator}
        onChange={handleOperatorChange}
        options={config.operators}
      />
      <div style={{ flex: "1 1 200px", minWidth: 200 }}>
        {renderValueInput(node.field, node.operator, node.value, (value) => onChange({ ...node, value }))}
      </div>
      <Button
        danger
        icon={<CloseOutlined />}
        onClick={onRemove}
        style={{ borderColor: "#fecaca", background: "#fef2f2", flexShrink: 0 }}
      />
    </div>
  );
}

function FilterGroup({ node, onChange, onRemove, depth, isRoot }) {
  const updateChild = (index, updated) => {
    const conditions = node.conditions.slice();
    conditions[index] = updated;
    onChange({ ...node, conditions });
  };

  const removeChild = (index) => {
    onChange({ ...node, conditions: node.conditions.filter((_, i) => i !== index) });
  };

  const addLeaf = () => onChange({ ...node, conditions: [...node.conditions, makeLeaf()] });
  const addGroup = () => onChange({ ...node, conditions: [...node.conditions, makeGroup()] });

  return (
    <div
      style={
        isRoot
          ? { marginBottom: 0 }
          : { borderLeft: "2px solid #cbd5e1", paddingLeft: 20, marginBottom: 15, position: "relative" }
      }
    >
      <div
        style={{
          marginBottom: 15,
          display: "flex",
          alignItems: "center",
          gap: 12,
          fontSize: 14,
          color: "#475569",
          flexWrap: "wrap",
        }}
      >
        <Text style={{ fontSize: 14, color: "#475569" }}>Match</Text>
        <Select
          size="middle"
          value={node.operator}
          onChange={(v) => onChange({ ...node, operator: v })}
          options={MATCH_OPTIONS}
          style={{ width: 90, fontWeight: 600 }}
        />
        <Text style={{ fontSize: 14, color: "#475569" }}>of the following rules:</Text>
        <Button size="small" icon={<PlusOutlined />} onClick={addLeaf}>
          Add Rule
        </Button>
        {depth < MAX_DEPTH - 1 && (
          <Button size="small" icon={<PlusOutlined />} onClick={addGroup}>
            Add Group
          </Button>
        )}
        {!isRoot && (
          <Button
            size="small"
            danger
            onClick={onRemove}
            style={{ borderColor: "#fecaca", background: "#fef2f2" }}
          >
            × Remove Group
          </Button>
        )}
      </div>

      <div>
        {node.conditions.map((child, i) =>
          child.type === "group" ? (
            <FilterGroup
              key={child.id}
              node={child}
              depth={depth + 1}
              onChange={(u) => updateChild(i, u)}
              onRemove={() => removeChild(i)}
            />
          ) : (
            <FilterLeaf key={child.id} node={child} onChange={(u) => updateChild(i, u)} onRemove={() => removeChild(i)} />
          ),
        )}
      </div>
    </div>
  );
}

/**
 * Recursive AND/OR condition-tree builder for the trace-log `filters` param
 * (requirements/API055, ioo-338). Controlled: `value` is the local tree
 * (with `id`/`type` bookkeeping), `onChange` receives the updated tree.
 * Use `serializeFilterTree(value)` to get the API-shaped payload.
 */
export function AdvancedFilterBuilder({ value, onChange }) {
  const leafCount = countLeaves(value);
  const depth = treeDepth(value);
  const overLimit = leafCount > MAX_CONDITIONS || depth > MAX_DEPTH;

  return (
    <div>
      <FilterGroup node={value} depth={0} isRoot onChange={onChange} onRemove={() => {}} />
      <Text
        type={overLimit ? "danger" : "secondary"}
        style={{ fontSize: 12, marginTop: 8, display: "block" }}
      >
        {leafCount} condition{leafCount === 1 ? "" : "s"} · nesting depth {depth} (max {MAX_DEPTH}, max{" "}
        {MAX_CONDITIONS} conditions)
      </Text>
    </div>
  );
}
